import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import { AuthProvider } from './context/AuthContext';
import { FacilityProvider } from './context/FacilityContext';
import { BookingProvider } from './context/BookingContext';
import { MODULES } from './constants/modules';
import { ThemeProvider } from './context/ThemeContext';
import Home from './pages/Home';
import Login from './pages/Login';
import ConfigureFacility from './pages/ConfigureFacility';
import Configuration from './pages/Configuration';
import ConfigureSlotRule from './pages/config/ConfigureSlotRule';
import ConfigIndServ from './pages/ConfigIndServ';
import BookingAgenda from './features/agenda/BookingAgenda';
import MyBookings from './pages/MyBookings';
import BookingNew from './pages/BookingNew';
import PaymentsManagement from './pages/PaymentsManagement';
import Reports from './pages/Reports';
import HelpCenter from './pages/HelpCenter';
import CreateTicket from './pages/CreateTicket';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Grounds from './pages/Grounds';
import FacilityServices from './pages/FacilityServices';
import BookingV2 from './pages/BookingV2';
import EquipmentList from './modules/operations/EquipmentList';
import AddEditEquipment from './modules/operations/AddEditEquipment';
import EquipmentDetails from './modules/operations/EquipmentDetails';
import DailyOperations from './modules/operations/DailyOperations';
import OperationsDashboard from './modules/operations/OperationsDashboard';
import AssetOverview from './modules/assets/AssetOverview';
import InventoryList from './modules/assets/InventoryList';
import InventoryDetails from './modules/assets/InventoryDetails';
import AddEditInventoryItem from './modules/assets/AddEditInventoryItem';
import StaffList from './modules/staff/StaffList';
import AddEditStaff from './modules/staff/AddEditStaff';
import StaffDetails from './modules/staff/StaffDetails';
import Attendance from './modules/staff/Attendance';
import SalaryManagement from './modules/staff/SalaryManagement';
import ExpenseList from './modules/expenses/ExpenseList';
import AddExpense from './modules/expenses/AddExpense';
import ExpenseDetails from './modules/expenses/ExpenseDetails';
import ExpenseCategories from './modules/expenses/ExpenseCategories';
import ExpenseAnalytics from './modules/expenses/ExpenseAnalytics';
import Prelaunch from './pages/Prelaunch';
import About from './pages/About';
import Contact from './pages/Contact';
import WebOnlyLandingRoute from './components/landing/WebOnlyLandingRoute';
import ProfilePage from './pages/ProfilePage';
import Settings from './pages/Settings';
import LoginAnalytics from './pages/LoginAnalytics';
import SurveyResponses from './pages/SurveyResponses';
import Subscriptions from './pages/Subscriptions';
import AuditLogs from './pages/AuditLogs';
import PublicBookingForm from './components/booking/PublicBookingForm';
import BookingApprovals from './pages/BookingApprovals';
import TournamentsDashboard from './pages/TournamentsDashboard';
import PublicTournamentRegister from './pages/PublicTournamentRegister';
import TournamentHome from './features/tournament/TournamentHome';
import TournamentDetail from './features/tournament/TournamentDetail';
import PlayApp from './play/PlayApp';
import FacilityLocation from './pages/FacilityLocation';
import OnboardingWizard from './pages/OnboardingWizard';
import Waitlist from './pages/Waitlist';
import SelfieCapture from './modules/selfie-attendance/SelfieCapture';
import AttendanceConfirmation from './modules/selfie-attendance/AttendanceConfirmation';
import SelfieAttendanceHistory from './modules/selfie-attendance/SelfieAttendanceHistory';
import SelfieAttendanceRecords from './modules/selfie-attendance/SelfieAttendanceRecords';
import RegisterFace from './modules/selfie-attendance/RegisterFace';

const EquipmentLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/dashboard/assets/fixed/${id}`} replace />;
};

const P = ({ children, module, accessType, anyOf }) => (
  <ProtectedRoute module={module} accessType={accessType} anyOf={anyOf}>{children}</ProtectedRoute>
);

// Booking a secondary service (open nets, practice nets) routes to booking-new,
// while the primary cricket-match booking uses the `bookings` module from the same
// screen. Accept either write permission so a role with only `bookings:write`
// isn't walled off from half the booking calendar (issue #187).
const BOOKING_NEW_WRITE = [
  { module: MODULES.INDIVIDUAL_BOOKING, accessType: 'write' },
  { module: MODULES.BOOKINGS, accessType: 'write' },
];

const BookingV2Redirect = () => {
  const segs = window.location.pathname.split('/');
  const groundId = segs[segs.length - 2];
  const serviceId = segs[segs.length - 1];
  return <Navigate to={`/dashboard/booking/${groundId}/${serviceId}`} replace />;
};

const SA = ({ children }) => (
  <SuperAdminRoute>{children}</SuperAdminRoute>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FacilityProvider>
          <BookingProvider>
            <Router>
              <Navbar />
              <Routes>
            {/* Public — no login (share /enroll/:id or /register/tournament/:id) */}
            <Route path="/login" element={<Login />} />
            {/* Native shells (iOS/Android) go straight to the app; the
                marketing pre-launch page is web-only. */}
            <Route
              path="/"
              element={(
                <WebOnlyLandingRoute>
                  <Prelaunch />
                </WebOnlyLandingRoute>
              )}
            />
            <Route
              path="/about"
              element={(
                <WebOnlyLandingRoute>
                  <About />
                </WebOnlyLandingRoute>
              )}
            />
            <Route
              path="/contact"
              element={(
                <WebOnlyLandingRoute>
                  <Contact />
                </WebOnlyLandingRoute>
              )}
            />
            <Route path="/book/:facilityId/:serviceId" element={<PublicBookingForm />} />
            <Route path="/enroll/:tournamentId" element={<PublicTournamentRegister />} />
            <Route path="/register/tournament/:tournamentId" element={<PublicTournamentRegister />} />
            <Route path="/play/*" element={<PlayApp />} />

            {/* ---- Super Admin: /superadmin/* ---- */}
            <Route path="/superadmin/facilities" element={<SA><ConfigureFacility /></SA>} />
            <Route path="/superadmin/users" element={<SA><Users /></SA>} />
            <Route path="/superadmin/roles" element={<SA><Roles /></SA>} />
            <Route path="/superadmin/config" element={<SA><Configuration /></SA>} />
            <Route path="/superadmin/settings" element={<SA><Settings /></SA>} />
            <Route path="/superadmin/analytics/logins" element={<SA><LoginAnalytics /></SA>} />
            <Route path="/superadmin/analytics/audit" element={<SA><AuditLogs /></SA>} />
            <Route path="/superadmin/survey-responses" element={<SA><SurveyResponses /></SA>} />
            <Route path="/superadmin/subscriptions" element={<SA><Subscriptions /></SA>} />

            {/* Legacy redirect: old /configuration -> new path */}
            <Route path="/configuration" element={<Navigate to="/superadmin/facilities" replace />} />

            {/* ---- Facility dashboard: /dashboard/* ---- */}
            <Route path="/dashboard" element={<P module={MODULES.DASHBOARD}><Home /></P>} />
            <Route path="/dashboard/profile" element={<P module={MODULES.DASHBOARD}><ProfilePage /></P>} />
            <Route path="/dashboard/settings" element={<P module={MODULES.DASHBOARD}><Settings /></P>} />
            {/* Legacy slot-config wizard retired — Facility Services is the single
                home for add + timings/pricing + edit. Redirect old paths there. */}
            <Route path="/dashboard/configure-service" element={<Navigate to="/dashboard/facility-services" replace />} />
            <Route path="/dashboard/slot-rule" element={<P module={MODULES.CONFIGURATION} accessType="write"><ConfigureSlotRule /></P>} />
            <Route path="/dashboard/slot-config" element={<Navigate to="/dashboard/facility-services" replace />} />
            <Route path="/dashboard/slot-configuration" element={<Navigate to="/dashboard/facility-services" replace />} />
            <Route path="/dashboard/config-individual-service" element={<P module={MODULES.CONFIGURATION}><ConfigIndServ /></P>} />
            {/* Booked/Active Slots retired — the day agenda's status chips replace them */}
            <Route path="/dashboard/active-slots" element={<Navigate to="/dashboard/book-now?status=booked" replace />} />
            <Route path="/dashboard/bookings-overview" element={<Navigate to="/dashboard/book-now" replace />} />
            <Route path="/dashboard/book-now" element={<P module={MODULES.BOOKINGS}><BookingAgenda /></P>} />
            <Route path="/dashboard/my-bookings" element={<P module={MODULES.BOOKINGS}><MyBookings /></P>} />
            <Route path="/dashboard/booking-new/:groundId/:serviceId" element={<P anyOf={BOOKING_NEW_WRITE}><BookingNew /></P>} />
            <Route path="/dashboard/booking-new" element={<P anyOf={BOOKING_NEW_WRITE}><BookingNew /></P>} />
            <Route path="/dashboard/individual-slot-booking" element={<P anyOf={BOOKING_NEW_WRITE}><BookingNew /></P>} />
            <Route path="/dashboard/payments" element={<P module={MODULES.PAYMENTS}><PaymentsManagement /></P>} />
            <Route path="/dashboard/reports" element={<P module={MODULES.REPORTS}><Reports /></P>} />
            <Route path="/dashboard/help-center" element={<P module={MODULES.HELP_CENTER}><HelpCenter /></P>} />
            <Route path="/dashboard/create-ticket" element={<P module={MODULES.HELP_CENTER}><CreateTicket /></P>} />
            <Route path="/dashboard/users" element={<P module={MODULES.USERS}><Users /></P>} />
            <Route path="/dashboard/roles" element={<P module={MODULES.ROLES}><Roles /></P>} />
            <Route path="/dashboard/grounds" element={<P module={MODULES.CONFIGURATION}><Grounds /></P>} />
            <Route path="/dashboard/onboarding" element={<P module={MODULES.CONFIGURATION} accessType="write"><OnboardingWizard /></P>} />
            <Route path="/dashboard/facility-services" element={<P module={MODULES.CONFIGURATION} accessType="write"><FacilityServices /></P>} />
            <Route path="/dashboard/facility-location" element={<P module={MODULES.CONFIGURATION} accessType="write"><FacilityLocation /></P>} />
            <Route path="/superadmin/facility-location" element={<SA><FacilityLocation /></SA>} />
            <Route path="/dashboard/booking/:groundId/:serviceId" element={<P module={MODULES.BOOKINGS}><BookingV2 /></P>} />
            <Route path="/dashboard/booking-v2/:groundId/:serviceId" element={<BookingV2Redirect />} />
            <Route path="/dashboard/booking-requests" element={<P module={MODULES.BOOKINGS}><BookingApprovals /></P>} />
            <Route path="/dashboard/tournaments" element={<P module={MODULES.TOURNAMENTS}><TournamentHome /></P>} />
            <Route path="/dashboard/tournaments/new" element={<P module={MODULES.TOURNAMENTS} accessType="write"><TournamentsDashboard /></P>} />
            <Route path="/dashboard/tournaments/:tournamentId/edit" element={<P module={MODULES.TOURNAMENTS} accessType="write"><TournamentsDashboard /></P>} />
            <Route path="/dashboard/tournaments/:tournamentId" element={<P module={MODULES.TOURNAMENTS}><TournamentDetail /></P>} />
            <Route path="/dashboard/waitlist" element={<P module={MODULES.BOOKINGS}><Waitlist /></P>} />

            {/* Operations */}
            <Route path="/dashboard/operations" element={<P module={MODULES.OPERATIONS}><OperationsDashboard /></P>} />
            <Route path="/dashboard/operations/daily" element={<P module={MODULES.OPERATIONS}><DailyOperations /></P>} />

            {/* Asset Management */}
            <Route path="/dashboard/assets" element={<P module={MODULES.EQUIPMENT}><AssetOverview /></P>} />
            <Route path="/dashboard/assets/fixed" element={<P module={MODULES.EQUIPMENT}><EquipmentList assetMode /></P>} />
            <Route path="/dashboard/assets/fixed/new" element={<P module={MODULES.EQUIPMENT} accessType="write"><AddEditEquipment /></P>} />
            <Route path="/dashboard/assets/fixed/:id/edit" element={<P module={MODULES.EQUIPMENT} accessType="write"><AddEditEquipment /></P>} />
            <Route path="/dashboard/assets/fixed/:id" element={<P module={MODULES.EQUIPMENT}><EquipmentDetails /></P>} />
            <Route path="/dashboard/assets/inventory" element={<P module={MODULES.EQUIPMENT}><InventoryList /></P>} />
            <Route path="/dashboard/assets/inventory/new" element={<P module={MODULES.EQUIPMENT} accessType="write"><AddEditInventoryItem /></P>} />
            <Route path="/dashboard/assets/inventory/:id" element={<P module={MODULES.EQUIPMENT}><InventoryDetails /></P>} />

            {/* Legacy equipment routes → redirect */}
            <Route path="/dashboard/operations/equipment" element={<Navigate to="/dashboard/assets/fixed" replace />} />
            <Route path="/dashboard/operations/equipment/new" element={<Navigate to="/dashboard/assets/fixed/new" replace />} />
            <Route path="/dashboard/operations/equipment/:id" element={<EquipmentLegacyRedirect />} />
            <Route path="/dashboard/operations/maintenance" element={<Navigate to="/dashboard/operations" replace />} />

            {/* Staff */}
            <Route path="/dashboard/staff" element={<P module={MODULES.STAFF}><StaffList /></P>} />
            <Route path="/dashboard/staff/new" element={<P module={MODULES.STAFF} accessType="write"><AddEditStaff /></P>} />
            <Route path="/dashboard/staff/attendance" element={<P module={MODULES.STAFF}><Attendance /></P>} />
            <Route path="/dashboard/staff/salary" element={<P module={MODULES.STAFF}><SalaryManagement /></P>} />
            <Route path="/dashboard/staff/:id" element={<P module={MODULES.STAFF}><StaffDetails /></P>} />
            <Route path="/dashboard/staff/:id/edit" element={<P module={MODULES.STAFF} accessType="write"><AddEditStaff /></P>} />

            {/* Selfie Attendance */}
            <Route path="/dashboard/selfie-attendance" element={<P module={MODULES.SELFIE_ATTENDANCE}><SelfieCapture /></P>} />
            <Route path="/dashboard/selfie-attendance/confirmation" element={<P module={MODULES.SELFIE_ATTENDANCE}><AttendanceConfirmation /></P>} />
            <Route path="/dashboard/selfie-attendance/history" element={<P module={MODULES.SELFIE_ATTENDANCE}><SelfieAttendanceHistory /></P>} />
            <Route path="/dashboard/selfie-attendance/records" element={<P module={MODULES.SELFIE_ATTENDANCE}><SelfieAttendanceRecords /></P>} />
            <Route path="/dashboard/selfie-attendance/register-face" element={<P module={MODULES.SELFIE_ATTENDANCE} accessType="write"><RegisterFace /></P>} />
            <Route path="/dashboard/selfie-attendance/register-face/:staffId" element={<P module={MODULES.SELFIE_ATTENDANCE} accessType="write"><RegisterFace /></P>} />

            {/* Expenses */}
            <Route path="/dashboard/expenses" element={<P module={MODULES.EXPENSES}><ExpenseList /></P>} />
            <Route path="/dashboard/expenses/new" element={<P module={MODULES.EXPENSES} accessType="write"><AddExpense /></P>} />
            <Route path="/dashboard/expenses/categories" element={<P module={MODULES.EXPENSES}><ExpenseCategories /></P>} />
            <Route path="/dashboard/expenses/analytics" element={<P module={MODULES.EXPENSES}><ExpenseAnalytics /></P>} />
            <Route path="/dashboard/expenses/:id/edit" element={<P module={MODULES.EXPENSES} accessType="write"><AddExpense /></P>} />
            <Route path="/dashboard/expenses/:id" element={<P module={MODULES.EXPENSES}><ExpenseDetails /></P>} />

            {/* Legacy redirects for old paths */}
            <Route path="/app" element={<Navigate to="/dashboard" replace />} />
            <Route path="/configure-service" element={<Navigate to="/dashboard/configure-service" replace />} />
            <Route path="/active-slots" element={<Navigate to="/dashboard/active-slots" replace />} />
            <Route path="/bookings-overview" element={<Navigate to="/dashboard/book-now" replace />} />
            <Route path="/my-bookings" element={<Navigate to="/dashboard/my-bookings" replace />} />
            <Route path="/booking-new" element={<Navigate to="/dashboard/booking-new" replace />} />
            <Route path="/payments-management" element={<Navigate to="/dashboard/payments" replace />} />
            <Route path="/reports" element={<Navigate to="/dashboard/reports" replace />} />
            <Route path="/help-center" element={<Navigate to="/dashboard/help-center" replace />} />
            <Route path="/create-ticket" element={<Navigate to="/dashboard/create-ticket" replace />} />
            <Route path="/users" element={<Navigate to="/dashboard/users" replace />} />
            <Route path="/roles" element={<Navigate to="/dashboard/roles" replace />} />
            <Route path="/grounds" element={<Navigate to="/dashboard/grounds" replace />} />
            <Route path="/operations" element={<Navigate to="/dashboard/operations" replace />} />
            <Route path="/operations/*" element={<Navigate to="/dashboard/operations" replace />} />
            <Route path="/staff" element={<Navigate to="/dashboard/staff" replace />} />
            <Route path="/staff/*" element={<Navigate to="/dashboard/staff" replace />} />
            <Route path="/expenses" element={<Navigate to="/dashboard/expenses" replace />} />
            <Route path="/expenses/*" element={<Navigate to="/dashboard/expenses" replace />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
          </BookingProvider>
        </FacilityProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
